import { useEffect, useMemo, useState, type ReactElement } from 'react';

import type { AdminApi, PublishedTemplateVersionDetail } from './admin-api';
import { problemMessage, problemTechnicalDetail } from './admin-presentation';
import {
  authoringRequiredRuleLabel,
  authoringValueTypeLabel,
} from './template-authoring-presentation';
import {
  assessmentLabel,
  templatePrimaryDisplayName,
} from './template-admin-presentation';
import { sortPublishedFieldsByOrdinal } from './template-published-field-order';

export type RuntimePreviewView = 'CONTRACTOR' | 'INSPECTOR';

export interface TemplateVersionRuntimePreviewProps {
  readonly api: AdminApi;
  readonly templateVersionId: string;
  readonly templateDisplayName: string;
  readonly versionCode: string;
  readonly isActive: boolean;
  readonly onBack: () => void;
}

function requiredBusinessLabel(rule: Record<string, unknown> | undefined): string {
  const type = rule !== undefined && typeof rule.type === 'string' ? rule.type : '';
  if (type.length === 0) {
    return 'Обязательность не указана';
  }
  return authoringRequiredRuleLabel(type);
}

function demoPlaceholder(valueType: string): string {
  switch (valueType) {
    case 'DATE':
    case 'DATETIME':
      return 'Будет заполнено при осмотре';
    case 'NUMBER':
      return 'Не заполнено';
    case 'ENUM':
      return 'Не выбрано';
    default:
      return 'Не заполнено';
  }
}

function sortFields(
  fields: NonNullable<PublishedTemplateVersionDetail['checklist_fields']>,
): typeof fields {
  return sortPublishedFieldsByOrdinal(fields);
}

export function TemplateVersionRuntimePreview(
  props: TemplateVersionRuntimePreviewProps,
): ReactElement {
  const [view, setView] = useState<RuntimePreviewView>('CONTRACTOR');
  const [detail, setDetail] = useState<PublishedTemplateVersionDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const loadSeq = useMemo(() => ({ current: 0 }), []);

  useEffect(() => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    setDetail(null);
    void props.api
      .getTemplateVersion(props.templateVersionId)
      .then((row) => {
        if (seq !== loadSeq.current) {
          return;
        }
        if (row.id !== props.templateVersionId) {
          setError(new Error('Получена другая версия чек-листа, чем запрошенная.'));
          setLoading(false);
          return;
        }
        setDetail(row);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq !== loadSeq.current) {
          return;
        }
        setError(failure);
        setLoading(false);
      });
  }, [props.api, props.templateVersionId, loadSeq]);

  const title = templatePrimaryDisplayName(props.templateDisplayName);
  const badge = props.isActive ? 'Активная' : 'Архивная';
  const versionCode =
    detail !== null && detail.version_code.trim().length > 0
      ? detail.version_code
      : props.versionCode;

  return (
    <section
      className="runtime-preview"
      aria-label="Предпросмотр опубликованной версии чек-листа"
      data-testid="published-runtime-preview"
    >
      <button type="button" className="admin-back-link" onClick={props.onBack}>
        ← Назад к чек-листу
      </button>
      <header className="runtime-preview-header">
        <h2>{title}</h2>
        <p className="meta" data-testid="published-runtime-preview-scope">
          Источник: опубликованная версия {versionCode} · {badge}. Не является превью
          редактируемого черновика.
        </p>
        <p className="runtime-preview-version">
          Версия {versionCode}
          <span className="runtime-preview-badge">{badge}</span>
        </p>
        <p className="meta">
          Предпросмотр опубликованной версии чек-листа. Данные осмотра не изменяются.
        </p>
      </header>
      <div className="runtime-preview-tabs" role="tablist" aria-label="Режим предпросмотра">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'CONTRACTOR'}
          aria-pressed={view === 'CONTRACTOR'}
          className={
            view === 'CONTRACTOR'
              ? 'runtime-preview-mode-btn is-active'
              : 'runtime-preview-mode-btn'
          }
          onClick={() => setView('CONTRACTOR')}
        >
          Подрядчик
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'INSPECTOR'}
          aria-pressed={view === 'INSPECTOR'}
          className={
            view === 'INSPECTOR'
              ? 'runtime-preview-mode-btn is-active'
              : 'runtime-preview-mode-btn'
          }
          onClick={() => setView('INSPECTOR')}
        >
          Инспектор
        </button>
      </div>
      {loading ? <p role="status">Загрузка версии…</p> : null}
      {error !== null ? (
        <div role="alert" className="banner banner-closed">
          <p>{problemMessage(error)}</p>
          {problemTechnicalDetail(error) !== null ? (
            <p className="meta">Технические сведения: {problemTechnicalDetail(error)}</p>
          ) : null}
        </div>
      ) : null}
      {!loading && detail !== null && view === 'CONTRACTOR' ? (
        <ContractorRuntimePreview detail={detail} />
      ) : null}
      {!loading && detail !== null && view === 'INSPECTOR' ? (
        <InspectorRuntimePreview detail={detail} />
      ) : null}
    </section>
  );
}

function ContractorRuntimePreview(props: {
  readonly detail: PublishedTemplateVersionDetail;
}): ReactElement {
  const fields = sortFields(props.detail.checklist_fields ?? []).filter(
    (field) => field.owner_code === 'CONTRACTOR',
  );
  const docs = [...(props.detail.document_requirements ?? [])].sort(
    (a, b) => a.ordinal - b.ordinal,
  );

  return (
    <div className="runtime-preview-body">
      <p className="meta">
        В реальном осмотре общие сведения из нескольких чек-листов могут объединяться.
      </p>
      <h3 className="section-title">Сведения для заполнения</h3>
      {fields.length === 0 ? (
        <p role="status" className="meta">
          В этой версии нет полей, которые заполняет подрядчик.
        </p>
      ) : (
        <ul className="runtime-preview-form" aria-label="Поля подрядчика">
          {fields.map((field) => (
            <li key={field.id} className="runtime-preview-field">
              <label>
                <span className="runtime-preview-field-label">
                  {field.label}
                  <span className="meta">
                    {' '}
                    · {requiredBusinessLabel(field.required_rule)}
                    {' · '}
                    {authoringValueTypeLabel(field.value_type)}
                  </span>
                </span>
                <input
                  type="text"
                  disabled
                  readOnly
                  value=""
                  placeholder={demoPlaceholder(field.value_type)}
                />
              </label>
            </li>
          ))}
        </ul>
      )}
      <h3 className="section-title">Документы</h3>
      {docs.length === 0 ? (
        <p role="status" className="meta">
          Требования к документам в этой версии не заданы.
        </p>
      ) : (
        <ul className="runtime-preview-docs" aria-label="Документы подрядчика">
          {docs.map((doc) => (
            <li key={doc.id} className="runtime-preview-doc">
              <p className="runtime-preview-field-label">
                {doc.display_label}
                <span className="meta"> · {requiredBusinessLabel(doc.required_rule)}</span>
              </p>
              {doc.metadata_fields.length > 0 ? (
                <ul className="runtime-preview-form">
                  {[...doc.metadata_fields]
                    .sort((a, b) => a.ordinal - b.ordinal)
                    .map((meta) => (
                      <li key={meta.id} className="runtime-preview-field">
                        <label>
                          <span className="runtime-preview-field-label">
                            {meta.label}
                            <span className="meta">
                              {' '}
                              · {meta.required ? 'Обязательно' : 'Необязательно'}
                            </span>
                          </span>
                          <input
                            type="text"
                            disabled
                            readOnly
                            value=""
                            placeholder={demoPlaceholder(meta.value_type)}
                          />
                        </label>
                      </li>
                    ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InspectorRuntimePreview(props: {
  readonly detail: PublishedTemplateVersionDetail;
}): ReactElement {
  const headerFields = sortFields(props.detail.checklist_fields ?? []).filter(
    (field) => field.owner_code === 'CONTRACTOR' || field.binding_kind === 'CASE_SNAPSHOT',
  );
  const inspectorFields = sortFields(props.detail.checklist_fields ?? []).filter(
    (field) => field.owner_code === 'INSPECTOR',
  );
  const sections = [...props.detail.sections].sort((a, b) => a.ordinal - b.ordinal);
  type ItemRow = PublishedTemplateVersionDetail['items'][number];
  const itemsBySection = new Map<string, ItemRow[]>();
  for (const item of props.detail.items) {
    const list = itemsBySection.get(item.section_code) ?? [];
    list.push(item);
    itemsBySection.set(item.section_code, list);
  }
  for (const [code, list] of itemsBySection) {
    itemsBySection.set(
      code,
      [...list].sort((a, b) => a.ordinal - b.ordinal),
    );
  }

  return (
    <div className="runtime-preview-body">
      <h3 className="section-title">Шапка осмотра</h3>
      {headerFields.length === 0 ? (
        <p className="meta" role="status">
          В опубликованной версии нет полей шапки для отображения.
        </p>
      ) : (
        <ul className="runtime-preview-form" aria-label="Поля шапки">
          {headerFields.map((field) => (
            <li key={field.id} className="runtime-preview-field">
              <span className="runtime-preview-field-label">{field.label}</span>
              <input
                type="text"
                disabled
                readOnly
                value=""
                placeholder="Значение из сведений осмотра"
              />
            </li>
          ))}
        </ul>
      )}

      {inspectorFields.length > 0 ? (
        <>
          <h3 className="section-title">Поля инспектора</h3>
          <ul className="runtime-preview-form" aria-label="Поля инспектора">
            {inspectorFields.map((field) => (
              <li key={field.id} className="runtime-preview-field">
                <label>
                  <span className="runtime-preview-field-label">
                    {field.label}
                    <span className="meta"> · {requiredBusinessLabel(field.required_rule)}</span>
                  </span>
                  <input
                    type="text"
                    disabled
                    readOnly
                    value=""
                    placeholder={demoPlaceholder(field.value_type)}
                  />
                </label>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3 className="section-title">Чек-лист</h3>
      {sections.length === 0 ? (
        <p className="meta" role="status">
          В этой версии нет разделов проверки.
        </p>
      ) : (
        sections.map((section) => {
          const items = itemsBySection.get(section.section_code) ?? [];
          const sectionTitle =
            section.title.trim().length > 0 ? section.title : `Раздел ${section.ordinal}`;
          return (
            <section
              key={section.section_code}
              className="runtime-preview-section"
              aria-label={sectionTitle}
            >
              <h4 className="runtime-preview-section-title">{sectionTitle}</h4>
              {items.length === 0 ? (
                <p className="meta">Пункты в разделе отсутствуют.</p>
              ) : (
                <ul className="runtime-preview-items">
                  {items.map((item) => (
                    <li key={item.item_code} className="runtime-preview-item">
                      <div className="runtime-preview-item-row">
                        <div className="runtime-preview-item-text">
                          <p className="runtime-preview-item-label">{item.label}</p>
                          {item.normative_reference !== null &&
                          item.normative_reference !== undefined &&
                          String(item.normative_reference).trim().length > 0 ? (
                            <p className="meta runtime-preview-item-ref">
                              {String(item.normative_reference)}
                            </p>
                          ) : null}
                        </div>
                        <div
                          className="runtime-preview-item-controls"
                          role="group"
                          aria-label={`Оценка: ${item.label}`}
                        >
                          <button type="button" className="runtime-preview-assessment-btn" disabled>
                            {assessmentLabel('PASS')}
                          </button>
                          <button type="button" className="runtime-preview-assessment-btn" disabled>
                            {assessmentLabel('FAIL')}
                          </button>
                          <button type="button" className="runtime-preview-assessment-btn" disabled>
                            {assessmentLabel('NOT_APPLICABLE')}
                          </button>
                          <button
                            type="button"
                            className="runtime-preview-comment-btn"
                            disabled
                            title="Комментарий инспектора"
                            aria-label="Комментарий инспектора"
                          >
                            <span aria-hidden="true">💬</span>
                            <span>Комментарий</span>
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
